/**
 * Every route in the application.
 *
 * Each protected route declares the `(screen, operation)` it needs, matching the
 * `iam.app_screen.key` the API uses. That makes the route table a readable
 * inventory of what exists and who may reach it — and keeps the guard next to
 * the thing it guards rather than in a list somewhere else that drifts.
 *
 * The guards are convenience. Authorisation happens twice on the server: the
 * handler asks `iam.can()`, then row-level security filters independently.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell.jsx';
import { RequireAuth, RequireAnonymous, RequirePermission } from './ProtectedRoute.jsx';

import LoginPage from '../features/auth/LoginPage.jsx';
import {
  DashboardRouter, RepDashboard, ManagerDashboard, ExecDashboard, AdminDashboard,
} from '../features/dashboard/DashboardPages.jsx';
import LeadsListPage from '../features/leads/LeadsListPage.jsx';
import LeadDetailPage from '../features/leads/LeadDetailPage.jsx';
import AccountsPage from '../features/accounts/AccountsPage.jsx';
import AccountDetailPage from '../features/accounts/AccountDetailPage.jsx';
import ContactsPage from '../features/contacts/ContactsPage.jsx';
import OpportunitiesPage from '../features/opportunities/OpportunitiesPage.jsx';
import QuotationsPage from '../features/quotations/QuotationsPage.jsx';
import { FeedPage } from '../features/activities/ActivitiesPages.jsx';
import ActivitiesPage from '../features/activities/ActivitiesPage.jsx';
import EmailTemplatesPage from '../features/emailTemplates/EmailTemplatesPage.jsx';
import EmailInboxPage from '../features/email/EmailInboxPage.jsx';
import TeamsPage from '../features/teams/TeamsPage.jsx';
import PartnersAdminPage from '../features/partners/PartnersAdminPage.jsx';
import TeamDetailPage from '../features/teams/TeamDetailPage.jsx';
import HelpPage from '../features/help/HelpPage.jsx';
import { ReportsPage, TargetsPage, CampaignsPage } from '../features/reports/ReportsPages.jsx';
import {
  LeadsReportPage, OpportunitiesReportPage, ActivitiesReportPage,
  RevenueReportPage, ForecastReportPage, TerritoriesReportPage,
} from '../features/reports/ReportDetailPages.jsx';
import UsersAdminPage from '../features/admin/UsersAdminPage.jsx';
import {
  WorkflowsPage, AuditPage, SecurityPage,
} from '../features/admin/AdminPages.jsx';
import MasterDataPage from '../features/admin/MasterDataPage.jsx';
import LetterheadPage from '../features/admin/LetterheadPage.jsx';
import {
  ProfilePage, SessionsPage, ChangePasswordPage, ForgotPasswordPage, ResetPasswordPage,
} from '../features/profile/ProfilePages.jsx';
import { EmptyState } from '../components/common/index.jsx';

/** Wrap a screen in its permission gate. Keeps the route table one line per route. */
const gated = (screen, element, operation = 'view') => (
  <RequirePermission screen={screen} operation={operation}>{element}</RequirePermission>
);

export default function AppRoutes() {
  return (
    <Routes>
      {/* ---- unauthenticated ---- */}
      <Route path="/login" element={<RequireAnonymous><LoginPage /></RequireAnonymous>} />
      <Route path="/forgot-password" element={<RequireAnonymous><ForgotPasswordPage /></RequireAnonymous>} />
      <Route path="/reset-password" element={<RequireAnonymous><ResetPasswordPage /></RequireAnonymous>} />

      {/* ---- everything else lives inside the shell ---- */}
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        {/* Workspace */}
        <Route index element={<DashboardRouter />} />
        <Route path="dashboard/rep" element={<RepDashboard />} />
        <Route path="dashboard/manager" element={<ManagerDashboard />} />
        <Route path="dashboard/exec" element={<ExecDashboard />} />
        <Route path="dashboard/admin" element={<AdminDashboard />} />
        <Route path="feed" element={gated('feed', <FeedPage />)} />
        {/* Calendar is now a view toggle on Activities, not its own screen — redirect old links. */}
        <Route path="calendar" element={<Navigate to="/activities?view=calendar" replace />} />
        <Route path="teams" element={gated('teams', <TeamsPage />)} />
        <Route path="teams/:id" element={gated('teams', <TeamDetailPage />)} />

        {/* Sell */}
        <Route path="leads" element={gated('leads', <LeadsListPage />)} />
        <Route path="leads/:id" element={gated('leads', <LeadDetailPage />)} />
        <Route path="accounts" element={gated('accounts', <AccountsPage />)} />
        <Route path="accounts/:id" element={gated('accounts', <AccountDetailPage />)} />
        <Route path="contacts" element={gated('contacts', <ContactsPage />)} />
        <Route path="opportunities" element={gated('opportunities', <OpportunitiesPage />)} />
        {/* Pipeline is now a view toggle on Opportunities, not its own screen — redirect old links. */}
        <Route path="pipeline" element={<Navigate to="/opportunities?view=board" replace />} />
        <Route path="quotations" element={gated('quotations', <QuotationsPage />)} />

        {/* Engage */}
        <Route path="activities" element={gated('activities', <ActivitiesPage />)} />
        {/* Follow-ups is now a view toggle on Activities, not its own screen — redirect old links. */}
        <Route path="follow-ups" element={<Navigate to="/activities?view=followups" replace />} />
        <Route path="email-templates" element={gated('templates', <EmailTemplatesPage />)} />
        <Route path="email" element={gated('email', <EmailInboxPage />)} />
        <Route path="campaigns" element={gated('campaigns', <CampaignsPage />)} />

        {/* Analyse */}
        <Route path="reports" element={gated('reports', <ReportsPage />)} />
        <Route path="reports/leads" element={gated('reports', <LeadsReportPage />)} />
        <Route path="reports/opportunities" element={gated('reports', <OpportunitiesReportPage />)} />
        <Route path="reports/activities" element={gated('reports', <ActivitiesReportPage />)} />
        <Route path="reports/revenue" element={gated('reports', <RevenueReportPage />)} />
        <Route path="reports/forecast" element={gated('reports', <ForecastReportPage />)} />
        <Route path="reports/territories" element={gated('reports', <TerritoriesReportPage />)} />
        {/* Forecast/Territories used to be their own top-level screens (`forecast`/`territory`)
            — now real reports gated on `reports:view` alongside the rest; old links redirect. */}
        <Route path="forecast" element={<Navigate to="/reports/forecast" replace />} />
        <Route path="territories" element={<Navigate to="/reports/territories" replace />} />
        <Route path="targets" element={gated('targets', <TargetsPage />)} />

        {/* Administration */}
        <Route path="admin/users" element={gated('admin.users', <UsersAdminPage />)} />
        <Route path="admin/partners" element={gated('admin.partners', <PartnersAdminPage />)} />
        <Route path="admin/master-data" element={gated('admin.master', <MasterDataPage />)} />
        <Route path="admin/letterhead" element={gated('admin.master', <LetterheadPage />)} />
        <Route path="admin/workflows" element={gated('admin.workflow', <WorkflowsPage />)} />
        <Route path="admin/audit" element={gated('admin.audit', <AuditPage />)} />
        <Route path="admin/security" element={gated('admin.security', <SecurityPage />)} />

        {/* Your own account — no permission needed; every user may manage themselves. */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="profile/sessions" element={<SessionsPage />} />
        <Route path="profile/password" element={<ChangePasswordPage />} />

        {/* A user manual isn't a permission either — reachable by every role,
            same reasoning as the profile routes above. See navModel.js's
            `screen: null` Help item for the matching sidebar entry. */}
        <Route path="help" element={<HelpPage />} />

        <Route
          path="*"
          element={
            <EmptyState
              icon="bi-signpost-split"
              title="No such page"
              message="The address does not match any screen in this application."
            />
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
